import { useQuery } from "@tanstack/react-query";
import { AUDITED_READ_OPTIONS } from "@/shared/api/audited-read";
import { httpClient } from "@/shared/api/http-client";
import type { Paged } from "@/shared/types/paged";
import type { OrganizationKind } from "./organization-link";

export const ORGANIZATION_SEARCH_MIN_LENGTH = 2;
const ORGANIZATION_SEARCH_MAX_LENGTH = 100;
const ORGANIZATION_SEARCH_LIMIT = 20;

/** The slice of an admin organization list row the picker needs. */
export interface OrganizationOption {
  readonly id: string;
  readonly name: string;
  readonly kind: OrganizationKind;
  readonly active: boolean;
  readonly isTest: boolean;
  readonly owner: { readonly id: string; readonly displayName: string };
}

export interface OrganizationSearch {
  readonly organizations: readonly OrganizationOption[];
  /** `false` while the term is too short to query. */
  readonly isSearchable: boolean;
  readonly isLoading: boolean;
  readonly isError: boolean;
}

/**
 * Search organizations by name (or owner name) for the "add to organization"
 * picker: `GET /api/admin/organizations?search=`. Deactivated organizations
 * are dropped because the API refuses to add members to them. Audited read:
 * it only fires for a real term, never retries.
 */
export function useOrganizationSearch(search: string): OrganizationSearch {
  const term = search.trim().slice(0, ORGANIZATION_SEARCH_MAX_LENGTH).trim();
  const isSearchable = term.length >= ORGANIZATION_SEARCH_MIN_LENGTH;
  const query = useQuery({
    queryKey: ["admin", "organizations", "picker-search", term] as const,
    queryFn: () => {
      const params = new URLSearchParams({
        page: "1",
        limit: String(ORGANIZATION_SEARCH_LIMIT),
        search: term,
      });
      return httpClient<Paged<OrganizationOption>>(`/api/admin/organizations?${params}`);
    },
    enabled: isSearchable,
    ...AUDITED_READ_OPTIONS,
  });
  return {
    organizations: isSearchable ? (query.data?.items ?? []).filter((o) => o.active) : [],
    isSearchable,
    isLoading: isSearchable && query.isLoading,
    isError: isSearchable && query.isError,
  };
}
